import {
  collection,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { tratarErro, tratarErroDeCallable } from '@/lib/erros';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


/** Clínicas do SaaS. Só o superadmin consegue listar — as rules cuidam disso. */
export function ouvirClinicas(aoReceber, aoFalhar) {
  return onSnapshot(
    query(collection(db, 'clinicas'), orderBy('nome')),
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('as clínicas', aoFalhar),
  );
}

export function ouvirPlanos(aoReceber, aoFalhar) {
  return onSnapshot(
    collection(db, 'planos'),
    (snap) => {
      const planos = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.precoMensal ?? 0) - (b.precoMensal ?? 0));
      aoReceber(planos);
    },
    tratarErro('os planos', aoFalhar),
  );
}

/**
 * Contagens de uso por clínica. Usa agregação no servidor — trazer os
 * documentos só para contá-los ficaria caro assim que as clínicas crescerem.
 */
export async function contarUso(clinicaId) {
  const contar = async (caminho) => {
    try {
      const snap = await getCountFromServer(collection(db, 'clinicas', clinicaId, caminho));
      return snap.data().count;
    } catch (e) {
      // ★ WT 1b: `null` continua sendo a reserva (a tela mostra "—"), mas a falha
      // passa a ser registrada: contagem ausente no painel do SaaS era invisível.
      tratarErro(`a contagem de ${caminho}`, null, 'service')(e);
      return null;
    }
  };
  const [leads, usuarios, agendamentos] = await Promise.all([
    contar('leads'),
    contar('usuarios'),
    contar('agendamentos'),
  ]);
  return { leads, usuarios, agendamentos };
}

export const criarClinica = (dados) => chamarCallable('criarClinica')(dados);

export async function salvarPlano(planoId, dados) {
  const numero = (v) => Number(String(v ?? '').replace(',', '.')) || 0;
  await setDoc(
    doc(db, 'planos', planoId),
    {
      nome: String(dados.nome ?? '').trim(),
      precoMensal: numero(dados.precoMensal),
      limites: {
        usuarios: numero(dados.usuarios),
        numerosWhatsapp: numero(dados.numerosWhatsapp),
        mensagensIA: numero(dados.mensagensIA),
        minutosVoz: numero(dados.minutosVoz),
      },
    },
    { merge: true },
  );
}

/**
 * MRR: soma da mensalidade dos planos das clínicas ativas. Clínica sem plano
 * conhecido não entra — é melhor a receita aparecer menor do que inventada.
 */
export function calcularMrr(clinicas, planos) {
  const preco = new Map(planos.map((p) => [p.id, Number(p.precoMensal) || 0]));
  let mrr = 0;
  let semPlano = 0;
  for (const clinica of clinicas) {
    if (clinica.ativa === false) continue;
    if (!preco.has(clinica.planoId)) {
      semPlano += 1;
      continue;
    }
    mrr += preco.get(clinica.planoId);
  }
  return { mrr, semPlano };
}

/**
 * ★★★ WT lote 1 — OS ERROS DE UMA CLÍNICA, para a tela de Saúde do sistema.
 *
 * ★ Consulta de UMA clínica por vez, não `collectionGroup`: o superadmin escolhe a clínica,
 * e varrer todas de uma vez custaria uma leitura por erro de todo o SaaS.
 *
 * ⚠ `orderBy('criadoEm','desc') + limit(500)`: ordenação de **um campo só** sobre uma
 * coleção — índice de campo único, que o Firestore cria sozinho. ★ **Nenhum índice composto
 * é preciso**, e por isso esta tela não depende de deploy de índices.
 *
 * ★ `limit(500)` é consciente: a tela agrupa e mostra contagens, então 500 registros
 * recentes bastam para ver padrão. ⚠ E o teto por sessão (20/h) já impede que uma clínica
 * sozinha afogue a lista.
 */
export async function buscarErrosDaClinica(clinicaId, { limite = 500 } = {}) {
  const snap = await getDocs(query(
    collection(db, 'clinicas', clinicaId, 'erros'),
    orderBy('criadoEm', 'desc'),
    limit(limite),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
